import { FC } from 'react';
import clsx from 'clsx';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { EnrollmentCardContainerProps, EnrollmentCardIcon } from './EnrollmentCardContainer.types';
import { EnrollmentCard } from '../../molecules/EnrollmentCard';
import { Amenity } from '../../molecules/EnrollmentCard/Amenity';
import { sanitizeTrackingValue } from '../../utils/polarisTrackingUtils';

function buttonTarget(openInaNewTab?: boolean): '_blank' | '_self' {
  return openInaNewTab ? '_blank' : '_self';
}

function buildEnrollmentTrackingValue(
  trackingProperties: EnrollmentCardContainerProps['trackingProperties'],
  ctaText?: string,
  openInNewTab?: boolean
): string | undefined {
  if (!trackingProperties?.trackingContentPosition || !trackingProperties?.trackingDescription || !ctaText)
    return undefined;

  const ctaSuffix = sanitizeTrackingValue(ctaText);
  const linkType = openInNewTab ? 'external' : 'internal';

  return `${trackingProperties.trackingContentPosition}|${trackingProperties.trackingDescription}${
    ctaSuffix ? `-${ctaSuffix}` : ''
  }|${linkType}`;
}

function extractIconsFromProps<Props>(props: Record<string, Props>): EnrollmentCardIcon[] {
  const icons: EnrollmentCardIcon[] = [];
  const pathKeys = Object.keys(props).filter(key => key.startsWith('iconPath_'));
  const normalizeIconPath = (pathValue: unknown): string => {
    if (typeof pathValue === 'string') {
      return pathValue;
    }
    if (Array.isArray(pathValue)) {
      const firstValidPath = pathValue.find(
        (iconPath): iconPath is string => typeof iconPath === 'string' && iconPath.trim().length > 0
      );
      return firstValidPath ?? '';
    }
    return '';
  };

  pathKeys.forEach(pathKey => {
    const index = pathKey.split('_')[1];
    const icon: EnrollmentCardIcon = {
      path: normalizeIconPath(props[`iconPath_${index}`]),
      altText: (props[`iconAltText_${index}`] as string) || '',
      iconOrder: [
        normalizeIconPath(props[`iconPath_1`]),
        normalizeIconPath(props[`iconPath_2`]),
        normalizeIconPath(props[`iconPath_3`]),
        normalizeIconPath(props[`iconPath_4`]),
      ],
    };
    icons.push(icon);
  });
  return icons;
}

export const EnrollmentCardContainerComp: FC<EnrollmentCardContainerProps & { className?: string; theme: string }> = ({
  className,
  header,
  primaryCtaLinkText,
  primaryCtaLink,
  primaryCtaOpenInNewTab,
  secondaryCtaLinkText,
  secondaryCtaLink,
  secondaryCtaOpenInNewTab,
  enrollmentCardDescription,
  altText,
  dynamicMedia,
  fileReferenceImage,
  styleclass,
  theme,
  trackingProperties,
  // Logged-in / logged-out variant fields from CF
  headerLoggedIn,
  headerLoggedOut,
  descriptionLoggedIn,
  descriptionLoggedOut,
  ctaLinkLoggedIn,
  ctaLinkTextLoggedIn,
  ctaOpenInNewTabLoggedIn,
  isSlotLoading = false,
  ...rest
}) => {
  const isSignedInUser = useUserDetailsGlobalStore(state => state?.isSignedInUser);
  const isUserDetailsSaved = useUserDetailsGlobalStore(state => state?.isUserDetailsSaved);
  const isCardLoading = !isUserDetailsSaved || isSlotLoading;
  const loggedInVariant = Boolean(isSignedInUser);
  const loggedOutVariant = !loggedInVariant;

  // Resolve logged-in / logged-out variant fields
  const resolvedHeader = (loggedInVariant ? headerLoggedIn : headerLoggedOut) || header;
  const resolvedDescription =
    (loggedInVariant ? descriptionLoggedIn : descriptionLoggedOut) || enrollmentCardDescription;
  const resolvedCtaLink = (loggedInVariant ? ctaLinkLoggedIn : primaryCtaLink) || primaryCtaLink;
  const resolvedCtaLinkText = (loggedInVariant ? ctaLinkTextLoggedIn : primaryCtaLinkText) || primaryCtaLinkText;
  const resolvedCtaOpenInNewTab = loggedInVariant
    ? ctaOpenInNewTabLoggedIn ?? primaryCtaOpenInNewTab
    : primaryCtaOpenInNewTab;

  const buttons = [
    {
      buttonCopy: resolvedCtaLinkText,
      className: `m-button-m m-button-primary${loggedInVariant ? '-inverse' : ''}`,
      href: resolvedCtaLink,
      target: buttonTarget(resolvedCtaOpenInNewTab),
      ariaLabel: resolvedCtaLinkText,
      trackingProperties,
      custom_click_track_value: buildEnrollmentTrackingValue(
        trackingProperties,
        resolvedCtaLinkText,
        resolvedCtaOpenInNewTab
      ),
    },
  ];

  const icons = extractIconsFromProps(rest);
  const hasSignedOutIcons = icons.some(({ path }) => path.trim().length > 0);
  const image = loggedOutVariant
    ? undefined
    : {
        altText,
        dynamicMedia,
        fileReferenceImage,
      };

  // Keep skeleton mounted while loading. After loading settles, suppress signed-out
  // card if icon payload is still unavailable.
  if (loggedOutVariant && !isCardLoading && !hasSignedOutIcons) {
    return null;
  }

  if (loggedOutVariant && secondaryCtaLinkText) {
    buttons.push({
      buttonCopy: secondaryCtaLinkText,
      className: 'm-button-m m-button-secondary',
      href: secondaryCtaLink,
      target: buttonTarget(secondaryCtaOpenInNewTab),
      ariaLabel: secondaryCtaLinkText,
      trackingProperties,
      custom_click_track_value: buildEnrollmentTrackingValue(
        trackingProperties,
        secondaryCtaLinkText,
        secondaryCtaOpenInNewTab
      ),
    });
  }
  return (
    <EnrollmentCard
      className={clsx(className, styleclass ?? '', theme)}
      buttons={buttons}
      header={resolvedHeader}
      description={resolvedDescription}
      isLoggedInVariant={loggedInVariant}
      isLoading={isCardLoading}
      image={image}
      theme={theme}
    >
      {loggedOutVariant &&
        icons
          .filter(({ path }) => path.trim().length > 0)
          .map(({ path, altText }, index) => {
            const [iconCategory, iconClass] = path.split(':');
            const updatedIconClass = iconClass?.split('/').pop();
            const iconClassName = updatedIconClass || iconCategory;
            return <Amenity key={`${path}-${altText}-${index}`} iconClass={iconClassName} label={altText} />;
          })}
    </EnrollmentCard>
  );
};
